import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { optimizedSrcSet } from "@/lib/imageOptimize";

interface ImageGalleryProps {
  images: (string | null)[];
  alt?: string;
  className?: string;
}

export function ImageGallery({ images, alt = "Product image", className = "" }: ImageGalleryProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  
  const validImages = images.filter((img): img is string => img !== null && img !== undefined);
  
  const handlePrevious = useCallback(() => {
    if (lightboxIndex !== null) {
      setImageLoading(true);
      setIsZoomed(false);
      setLightboxIndex((lightboxIndex - 1 + validImages.length) % validImages.length);
    }
  }, [lightboxIndex, validImages.length]);

  const handleNext = useCallback(() => {
    if (lightboxIndex !== null) {
      setImageLoading(true);
      setIsZoomed(false);
      setLightboxIndex((lightboxIndex + 1) % validImages.length);
    }
  }, [lightboxIndex, validImages.length]);

  const handleClose = useCallback(() => {
    setLightboxIndex(null);
    setIsZoomed(false);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, handleClose, handlePrevious, handleNext]);

  if (validImages.length === 0) {
    return (
      <div className={`aspect-square bg-muted rounded-lg overflow-hidden flex items-center justify-center ${className}`}>
        <p className="text-sm text-muted-foreground">No image available</p>
      </div>
    );
  }

  return (
    <>
      <div className={`space-y-3 ${className}`}>
        {/* Main Image */}
        <div 
          className="aspect-square bg-muted rounded-lg overflow-hidden cursor-pointer relative group"
          onClick={() => setLightboxIndex(currentImageIndex)}
          data-testid="main-product-image"
        >
          <picture>
            <source
              type="image/webp"
              srcSet={optimizedSrcSet(validImages[currentImageIndex], [400, 800, 1600]) || undefined}
              sizes="(max-width: 768px) 100vw, 50vw"
            />
            <img
              src={validImages[currentImageIndex]}
              alt={alt}
              decoding="async"
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </picture>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center gap-2">
              <ZoomIn className="w-8 h-8 text-white" />
              <p className="text-white text-sm font-medium">Click to enlarge</p>
            </div>
          </div>
        </div>

        {/* Thumbnail Strip */}
        {validImages.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {validImages.map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all duration-200 ${
                  index === currentImageIndex 
                    ? 'border-primary shadow-lg scale-105' 
                    : 'border-border hover:border-primary/50 hover:scale-105'
                }`}
                data-testid={`thumbnail-${index}`}
              >
                <picture>
                  <source
                    type="image/webp"
                    srcSet={optimizedSrcSet(img, [400]) || undefined}
                    sizes="80px"
                  />
                  <img
                    src={img}
                    alt={`${alt} ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-contain"
                  />
                </picture>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Lightbox Dialog */}
      <Dialog open={lightboxIndex !== null} onOpenChange={handleClose}>
        <DialogContent className="max-w-[95vw] w-full h-[95vh] p-0 bg-black/98 backdrop-blur-sm border-none">
          <DialogTitle className="sr-only">Product Image Gallery</DialogTitle>
          <div className="relative w-full h-full flex flex-col">
            {/* Top Bar with Close Button and Counter */}
            <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
              <div className="flex-1" />
              <div className="bg-black/70 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium">
                {lightboxIndex !== null && `${lightboxIndex + 1} / ${validImages.length}`}
              </div>
              <div className="flex-1 flex justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20 backdrop-blur-sm rounded-full h-10 w-10"
                  onClick={handleClose}
                  data-testid="button-close-lightbox"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            {/* Main Image Container */}
            <div className="flex-1 flex items-center justify-center p-8 pt-20 pb-28">
              {lightboxIndex !== null && (
                <div 
                  className={`relative max-w-full max-h-full transition-all duration-300 cursor-pointer ${
                    isZoomed ? 'scale-150' : 'scale-100'
                  }`}
                  onClick={() => setIsZoomed(!isZoomed)}
                >
                  {imageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                    </div>
                  )}
                  <picture>
                    <source
                      type="image/webp"
                      srcSet={optimizedSrcSet(validImages[lightboxIndex], [800, 1600]) || undefined}
                      sizes="95vw"
                    />
                    <img
                      src={validImages[lightboxIndex]}
                      alt={`${alt} ${lightboxIndex + 1}`}
                      className={`max-w-full max-h-[calc(95vh-200px)] object-contain transition-opacity duration-300 ${
                        imageLoading ? 'opacity-0' : 'opacity-100'
                      }`}
                      onLoad={() => setImageLoading(false)}
                      data-testid="lightbox-image"
                    />
                  </picture>
                  {!isZoomed && !imageLoading && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-2 opacity-0 hover:opacity-100 transition-opacity">
                      <ZoomIn className="w-3 h-3" />
                      Click to zoom
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            {validImages.length > 1 && lightboxIndex !== null && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 backdrop-blur-md rounded-full h-12 w-12 z-10"
                  onClick={handlePrevious}
                  data-testid="button-previous-image"
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 backdrop-blur-md rounded-full h-12 w-12 z-10"
                  onClick={handleNext}
                  data-testid="button-next-image"
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              </>
            )}

            {/* Bottom Thumbnail Strip */}
            {validImages.length > 1 && lightboxIndex !== null && (
              <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex gap-3 overflow-x-auto p-4 justify-center scrollbar-hide">
                  {validImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setImageLoading(true);
                        setIsZoomed(false);
                        setLightboxIndex(index);
                      }}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        index === lightboxIndex 
                          ? 'border-white shadow-lg shadow-white/20 scale-110' 
                          : 'border-white/30 hover:border-white/60 hover:scale-105 opacity-70 hover:opacity-100'
                      }`}
                      data-testid={`lightbox-thumbnail-${index}`}
                    >
                      <picture>
                        <source
                          type="image/webp"
                          srcSet={optimizedSrcSet(img, [400]) || undefined}
                          sizes="64px"
                        />
                        <img
                          src={img}
                          alt={`Thumbnail ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-contain bg-black/40"
                        />
                      </picture>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
}
